import React, { FC } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useTranslate } from 'react-admin'
import { themeColor, xxllncColor } from '../../layout/theme/color'
import type { LastImportOut } from '../../types/apiRequests'

const StatPill: FC<{
  icon: React.ReactNode
  label: string
  value: number
  fg: string
  bg: string
}> = ({ icon, label, value, fg, bg }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      bgcolor: bg,
      color: fg,
      px: 1.5,
      py: 0.75,
      borderRadius: '999px',
      fontSize: 13,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      minWidth: 96,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography component="span" sx={{ fontWeight: 700, fontSize: 14 }}>
      {value}
    </Typography>
    <Typography
      component="span"
      sx={{ color: 'inherit', opacity: 0.75, fontSize: 12, fontWeight: 500 }}
    >
      {label}
    </Typography>
  </Stack>
)

const formatTimestamp = (iso?: string | null): string => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const LastImportCard: FC<{ data: LastImportOut | null }> = ({ data }) => {
  const translate = useTranslate()
  if (!data || !data.last_import_at) {
    return (
      <Typography variant="body2" sx={{ color: themeColor.slate60 }}>
        {translate('portalSettings.importExport.lastImport.emptyState')}
      </Typography>
    )
  }
  const counts: Record<string, number> = data.last_import_summary?.counts ?? {}
  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: themeColor.slate60 }}>
        {formatTimestamp(data.last_import_at)}
        {data.last_import_by
          ? ` — ${translate('portalSettings.importExport.lastImport.by', {
            actor: data.last_import_by,
          })}`
          : ''}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <StatPill
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
          label={translate('portalSettings.importExport.lastImport.statCreated')}
          value={counts.created || 0}
          fg={xxllncColor.green100}
          bg={xxllncColor.opGreen10}
        />
        <StatPill
          icon={<RestartAltIcon sx={{ fontSize: 16 }} />}
          label={translate('portalSettings.importExport.lastImport.statUpdated')}
          value={counts.updated || 0}
          fg={themeColor.blue}
          bg={themeColor.blue10}
        />
        <StatPill
          icon={<RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />}
          label={translate('portalSettings.importExport.lastImport.statSkipped')}
          value={counts.skipped || 0}
          fg={themeColor.slate60}
          bg={themeColor.slate05}
        />
        <StatPill
          icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
          label={translate('portalSettings.importExport.lastImport.statFailed')}
          value={counts.failed || 0}
          fg={xxllncColor.red100}
          bg={xxllncColor.opRed10}
        />
      </Stack>
      {data.last_import_summary?.bundle_filename && (
        <Typography variant="caption" sx={{ color: themeColor.slate60 }}>
          {translate('portalSettings.importExport.lastImport.bundle', {
            filename: data.last_import_summary.bundle_filename,
          })}
        </Typography>
      )}
    </Stack>
  )
}

export default LastImportCard
